/**
 * Cheat log — таблица `cheat_attempts`.
 *
 * Что здесь ЕСТЬ: случаи, когда виноват клиент. Либо присланная сумма не
 * сошлась с посчитанной сервером (`checksum_mismatch`), либо присланные
 * ходы невозможно разыграть (`invalid_moves`).
 *
 * Чего здесь НЕТ и не должно быть: боёв, отменённых из-за нашей же
 * выкатки. Если между стартом боя и его завершением поменялись константы
 * движка, бой закрывается со статусом `voided`, энергия возвращается,
 * и строки в этом журнале не появляется. Иначе после каждого деплоя
 * список заполнялся бы честными игроками — и смотреть в него перестали бы.
 */

import { useEffect, useState } from 'react'
import { request } from '../api/client.js'
import type { CheatRow } from '../api/types.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { PageHead } from '../components/Layout.js'
import { Card, formatDate, shortId } from '../components/ui.js'
import { navigate } from '../router.js'

const REASON_LABEL: Record<string, { text: string; tone: string }> = {
  checksum_mismatch: { text: 'checksum mismatch', tone: 'badge--danger' },
  invalid_moves: { text: 'impossible moves', tone: 'badge--warn' },
}

export function CheatLog(): JSX.Element {
  const [rows, setRows] = useState<CheatRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    request<{ rows: CheatRow[] }>('/admin/cheat-attempts', { query: { limit: 100 } })
      .then((data) => {
        if (cancelled) return
        setRows(data.rows)
        setError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const columns: Column<CheatRow>[] = [
    { key: 'when', title: 'When', render: (row) => formatDate(row.createdAt) },
    {
      key: 'player',
      title: 'Player',
      render: (row) => (
        <div>
          <div>{row.email ?? '—'}</div>
          <div className="mono muted">{shortId(row.userId, 18)}</div>
        </div>
      ),
    },
    {
      key: 'battle',
      title: 'Battle',
      render: (row) => (
        <div>
          <div className="mono">{shortId(row.battleId, 18)}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {row.levelId ?? 'level deleted'}
            {row.battleStatus === null ? '' : ` · ${row.battleStatus}`}
          </div>
        </div>
      ),
    },
    {
      key: 'reason',
      title: 'Reason',
      render: (row) => {
        const label = REASON_LABEL[row.reason]
        return <span className={`badge ${label?.tone ?? ''}`}>{label?.text ?? row.reason}</span>
      },
    },
    {
      key: 'checksums',
      title: 'Sent / computed',
      render: (row) => (
        <div className="mono" style={{ fontSize: 11.5 }}>
          <div style={{ color: 'var(--danger)' }}>{row.gotChecksum}</div>
          <div className="muted">
            {/* Пусто — значит, ожидаемой суммы не существует: движок бросил
                на невозможном ходе, не доиграв бой до конца. */}
            {row.expectedChecksum ?? 'battle never finished'}
          </div>
        </div>
      ),
    },
    {
      key: 'result',
      title: 'Server computed',
      render: (row) => (
        <div style={{ fontSize: 12 }}>
          <div>{row.serverResult ?? '—'}</div>
          <div className="muted">
            {row.serverTurns === null ? '' : `${row.serverTurns} turns`}
            {row.moveCount === null ? '' : ` · ${row.moveCount} sent`}
          </div>
        </div>
      ),
    },
    {
      key: 'details',
      title: 'Details',
      render: (row) => (
        <span className="muted" style={{ whiteSpace: 'normal', display: 'inline-block', maxWidth: 320 }}>
          {row.details ?? '—'}
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHead
        title="Cheat log"
        subtitle="Battles whose result the server rejected. A mismatch caused by our own deploy never lands here: such a battle closes as voided, the energy is refunded, and the player is not at fault."
      />

      <Card title={`Attempts · ${String(rows.length)}`} padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row.id)}
          loading={loading}
          error={error}
          empty="Not a single rejected attempt — and that is good news"
          onRowClick={(row) => navigate({ name: 'player', id: row.userId })}
        />
      </Card>
    </>
  )
}
